import type { SimulationBatch, SimulationConversation } from '@/types/evaluator';
import { ISSUE_SCORE_THRESHOLD, SIMULATION_OUTCOME_LABELS } from './constants';
import type { AgentVersionDisplay, ConversationTurn, HistoryChartPoint, HistoryTimeRange, PersonaReviewItem, PersonaSortOption, ScoreDetail, ScoreSummary, VersionChartPoint } from './types';

export function formatDate(value?: string | null) {
  if (!value) return 'Unknown';
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

export function isCompleteStatus(status?: string | null) {
  const normalized = (status ?? '').toLowerCase();
  return normalized.includes('complete') || normalized === 'completed';
}

const TERMINAL_STATUSES = new Set([
  'completed', 'failed', 'cancelled',
  'workflow_complete', 'workflow_complete_no_changes',
  'workflow_complete_regression', 'workflow_complete_inconclusive',
  'optimization_complete',
  'scores_ready', 'awaiting_review',
]);

export function isRunningStatus(status?: string | null) {
  const normalized = (status ?? '').toLowerCase();
  if (!normalized) return false;
  return !TERMINAL_STATUSES.has(normalized);
}

export function getSimulationOutcome(status?: string | null): SimulationOutcome {
  const normalized = (status ?? '').toLowerCase();
  if (normalized.includes('fail')) return 'failed';
  if (normalized.includes('cancel')) return 'cancelled';
  if (isRunningStatus(normalized)) return 'running';
  return 'completed';
}

export function getStatusLabel(status: string) {
  return SIMULATION_OUTCOME_LABELS[getSimulationOutcome(status)];
}

export function getStatusClassName(status: string) {
  const outcome = getSimulationOutcome(status);
  if (outcome === 'failed') {
    return 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/70 dark:bg-red-950/30 dark:text-red-300';
  }
  if (outcome === 'cancelled') {
    return 'border-border bg-muted/40 text-muted-foreground';
  }
  if (outcome === 'running') {
    return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-300';
  }
  return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/30 dark:text-emerald-300';
}

export function getFriendlyRunName(run: SimulationBatch | null | undefined) {
  if (!run) return 'Simulation';
  const date = formatDate(run.created_at);
  const populations = run.dataset_names?.length ?? 0;
  return `${date} · ${populations} ${populations === 1 ? 'population' : 'populations'}`;
}

export function readString(input: Record<string, unknown>, key: string) {
  const value = input[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

export function normalizeAgentVersionNumber(value: string | null | undefined) {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^v?\d+$/.test(trimmed)) return `Agent v${trimmed.replace(/^v/i, '')}`;
  if (/^agent\s+v/i.test(trimmed)) return trimmed;
  return null;
}

export function normalizeAgentVersionTag(value: string | null | undefined) {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'production' || normalized === 'latest') return 'Production';
  if (normalized === 'staging') return 'Staging';
  if (normalized === 'preview') return 'Preview';
  return null;
}

export function getAgentVersionDisplay(run: SimulationBatch | null | undefined): AgentVersionDisplay {
  if (!run) return { version: '-', tag: null };

  const version = normalizeAgentVersionNumber(run.agent_version)
    ?? normalizeAgentVersionNumber(run.prompt_version)
    ?? normalizeAgentVersionNumber(readStringMetric(run.aggregate_metrics, 'applied_prompt_version'))
    ?? normalizeAgentVersionNumber(readStringMetric(run.aggregate_metrics, 'original_prompt_version'))
    ?? normalizeAgentVersionNumber(readStringMetric(run.aggregate_metrics, 'base_prompt_version'));

  const tag = normalizeAgentVersionTag(run.prompt_version)
    ?? normalizeAgentVersionTag(run.agent_version);

  const runVersions = (run.runs ?? [])
    .map((summary) => normalizeAgentVersionNumber(summary.agent_version) ?? normalizeAgentVersionNumber(summary.prompt_version))
    .filter((value): value is string => Boolean(value));
  const uniqueVersions = Array.from(new Set(runVersions));
  const runTags = (run.runs ?? [])
    .map((summary) => normalizeAgentVersionTag(summary.prompt_version) ?? normalizeAgentVersionTag(summary.agent_version))
    .filter((value): value is string => Boolean(value));
  const uniqueTags = Array.from(new Set(runTags));

  if (version) return { version, tag };
  if (uniqueVersions.length === 1) return { version: uniqueVersions[0], tag };
  if (uniqueVersions.length > 1) return { version: '-', tag: 'Multiple' };

  return {
    version: '-',
    tag: tag ?? (uniqueTags.length === 1 ? uniqueTags[0] : null),
  };
}

export function getPopulationLabel(name?: string | null) {
  if (!name || name === 'unknown') return 'Population';
  return name.replace(/^workflow[-_\s]*/i, '').replace(/[-_]+/g, ' ').trim() || 'Population';
}

export function formatScoreValue(score: number) {
  return Math.round(score > 1 ? score : score * 100);
}

export function getScoreTone(score?: number | null) {
  if (score == null) return 'neutral';
  if (score < ISSUE_SCORE_THRESHOLD) return 'negative';
  if (score < 85) return 'neutral';
  return 'positive';
}

export function getScoreClassName(score?: number | null) {
  const tone = getScoreTone(score);
  if (tone === 'negative') return 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/70 dark:bg-red-950/30 dark:text-red-300';
  if (tone === 'positive') return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/30 dark:text-emerald-300';
  return 'border-border bg-muted/40 text-muted-foreground';
}

export function prettifyKey(key: string) {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\bworkflow\b/gi, 'conversation')
    .replace(/\bdataset\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function parseConversation(output?: string | null): ConversationTurn[] {
  if (!output) return [];

  const turns: ConversationTurn[] = [];
  let current: ConversationTurn | null = null;

  output.split('\n').forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    const match = trimmed.match(/^(Agent|User|ToolCall):\s*(.*)$/);
    if (match) {
      if (current) turns.push(current);
      current = {
        speaker: match[1] as ConversationTurn['speaker'],
        text: match[2],
      };
      return;
    }

    if (current) {
      current.text = `${current.text}\n${trimmed}`.trim();
    } else {
      current = { speaker: 'Other', text: trimmed };
    }
  });

  if (current) turns.push(current);
  return turns;
}

export function getTurnLabel(speaker: ConversationTurn['speaker']) {
  if (speaker === 'User') return 'Visitor';
  if (speaker === 'ToolCall') return 'Action';
  return speaker;
}

export function formatActionText(text: string) {
  const actionName = text
    .replace(/\{.*$/, '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

  if (!actionName) return text;
  return actionName.replace(/\b\w/g, (char) => char.toUpperCase());
}

export function readStringMetric(metrics: Record<string, unknown> | null | undefined, key: string) {
  const value = metrics?.[key];
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

export function getPersonaLabel(inputVars: Record<string, unknown>) {
  const name = readString(inputVars, 'name');
  const title = readString(inputVars, 'title');
  if (name && title) return `${name}, ${title}`;
  if (name ?? title) return name ?? title ?? 'Simulated visitor';

  const persona = readString(inputVars, 'persona');
  if (persona) {
    const roleMatch = persona.match(/^(.+?)\s+at\s+/i);
    if (roleMatch?.[1] && roleMatch[1].length <= 80) return roleMatch[1];
    const commaLead = persona.split(',')[0]?.trim();
    if (commaLead && commaLead.length <= 80) return commaLead;
    return persona.length > 80 ? `${persona.slice(0, 77)}...` : persona;
  }

  return 'Simulated visitor';
}

export function getScoreSummary(scores?: Record<string, unknown> | null): ScoreSummary | null {
  if (!scores) return null;

  const details = Object.entries(scores)
    .map(([key, entry]) => {
      if (!entry || typeof entry !== 'object') return null;
      const score = (entry as { score?: unknown }).score;
      if (typeof score !== 'number') return null;
      const reasoning = (entry as { reasoning?: unknown; comment?: unknown; explanation?: unknown }).reasoning
        ?? (entry as { comment?: unknown }).comment
        ?? (entry as { explanation?: unknown }).explanation;
      return {
        key,
        label: prettifyKey(key),
        score: formatScoreValue(score),
        reasoning: typeof reasoning === 'string' && reasoning.trim() ? reasoning.trim() : undefined,
      };
    })
    .filter((detail): detail is ScoreDetail => detail !== null)
    .sort((a, b) => a.score - b.score);

  if (details.length === 0) return null;

  const average = Math.round(details.reduce((sum, detail) => sum + detail.score, 0) / details.length);
  return {
    average,
    count: details.length,
    details,
    weak: details.filter((detail) => detail.score < ISSUE_SCORE_THRESHOLD),
  };
}

export function getBatchAggregateAverage(run: SimulationBatch | null | undefined) {
  const scoreAverages = run?.aggregate_metrics?.score_averages;
  if (!scoreAverages || typeof scoreAverages !== 'object') return null;

  const values = Object.values(scoreAverages).filter((value): value is number => (
    typeof value === 'number' && Number.isFinite(value)
  ));
  if (values.length === 0) return null;

  return Math.round(values.reduce((sum, score) => sum + formatScoreValue(score), 0) / values.length);
}

export function combineScoreSummaries(summaries: Array<ScoreSummary | null>): ScoreSummary | null {
  const details = summaries.flatMap((summary) => summary?.details ?? []);
  if (details.length === 0) return null;
  const average = Math.round(details.reduce((sum, detail) => sum + detail.score, 0) / details.length);
  return {
    average,
    count: details.length,
    details: [...details].sort((a, b) => a.score - b.score),
    weak: details.filter((detail) => detail.score < ISSUE_SCORE_THRESHOLD).sort((a, b) => a.score - b.score),
  };
}

export function getConversationSortScore(conversation: SimulationConversation) {
  const summary = getScoreSummary(conversation.scores);
  if (summary) return summary.average;
  if (conversation.status.toLowerCase().includes('fail')) return -1;
  return 101;
}

export function getPersonaIssueSortScore(persona: PersonaReviewItem) {
  if (persona.score) return persona.score.average;
  if (persona.status.toLowerCase().includes('fail')) return -1;
  return 101;
}

export function sortPersonaReviewItems(items: PersonaReviewItem[], sortBy: PersonaSortOption) {
  return [...items].sort((a, b) => {
    if (sortBy === 'highest_score') {
      const scoreA = a.score?.average ?? -1;
      const scoreB = b.score?.average ?? -1;
      if (scoreA !== scoreB) return scoreB - scoreA;
      return (b.lastActivity ?? '').localeCompare(a.lastActivity ?? '');
    }

    if (sortBy === 'newest') {
      const dateDelta = (b.lastActivity ?? '').localeCompare(a.lastActivity ?? '');
      if (dateDelta !== 0) return dateDelta;
      return getPersonaIssueSortScore(a) - getPersonaIssueSortScore(b);
    }

    if (sortBy === 'name') {
      const nameDelta = a.label.localeCompare(b.label);
      if (nameDelta !== 0) return nameDelta;
      return getPersonaIssueSortScore(a) - getPersonaIssueSortScore(b);
    }

    const scoreA = getPersonaIssueSortScore(a);
    const scoreB = getPersonaIssueSortScore(b);
    if (scoreA !== scoreB) return scoreA - scoreB;
    return (b.lastActivity ?? '').localeCompare(a.lastActivity ?? '');
  });
}

export function buildPersonaReviewItems(conversations: SimulationConversation[]) {
  const groups = new Map<string, SimulationConversation[]>();

  conversations.forEach((conversation) => {
    const input = conversation.input_vars ?? {};
    const keyParts = [
      conversation.dataset_name,
      getPersonaLabel(input),
      readString(input, 'goal') ?? '',
      readString(input, 'company') ?? '',
    ];
    const key = keyParts.join('|');
    const existing = groups.get(key) ?? [];
    existing.push(conversation);
    groups.set(key, existing);
  });

  return Array.from(groups.entries()).map(([id, items]) => {
    const sortedConversations = [...items].sort((a, b) => {
      const scoreDelta = getConversationSortScore(a) - getConversationSortScore(b);
      if (scoreDelta !== 0) return scoreDelta;
      return (b.completed_at ?? b.created_at ?? '').localeCompare(a.completed_at ?? a.created_at ?? '');
    });
    const first = sortedConversations[0];
    const input = first.input_vars ?? {};
    const score = combineScoreSummaries(sortedConversations.map((conversation) => getScoreSummary(conversation.scores)));
    const hasFailure = sortedConversations.some((conversation) => conversation.status.toLowerCase().includes('fail'));
    const status = hasFailure ? 'failed' : first.status;
    return {
      id,
      label: getPersonaLabel(input),
      title: readString(input, 'title'),
      company: readString(input, 'company'),
      population: getPopulationLabel(first.dataset_name),
      goal: readString(input, 'goal'),
      painPoints: readString(input, 'pain_points'),
      constraints: readString(input, 'constraints'),
      status,
      score,
      conversations: sortedConversations,
      lastActivity: sortedConversations[0]?.completed_at ?? sortedConversations[0]?.created_at,
    };
  });
}

export function getConversationTitle(conversation: SimulationConversation, index: number) {
  const summary = getScoreSummary(conversation.scores);
  const completed = conversation.completed_at ?? conversation.created_at;
  if (summary) return `Conversation ${index + 1} · ${summary.average}`;
  return completed ? `Conversation ${index + 1} · ${formatDate(completed)}` : `Conversation ${index + 1}`;
}

export function chooseDefaultRun(runs: SimulationBatch[]) {
  return runs.find((run) => isCompleteStatus(run.status) && (run.dataset_names?.length ?? 0) > 0)
    ?? runs.find((run) => (run.dataset_names?.length ?? 0) > 0)
    ?? runs.find((run) => isCompleteStatus(run.status))
    ?? runs[0]
    ?? null;
}

export function getRunAverage(conversations: SimulationConversation[]) {
  const scores = conversations
    .map((conversation) => getScoreSummary(conversation.scores)?.average)
    .filter((score): score is number => typeof score === 'number');
  if (scores.length === 0) return null;
  return Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
}

export function getSimulationRunAverage(run: SimulationBatch | null | undefined) {
  if (!run) return null;
  return getRunAverage(run.conversations ?? []) ?? getBatchAggregateAverage(run);
}

export function getSimulationConversationCount(run: SimulationBatch | null | undefined) {
  if (!run) return 0;
  const aggregateCount = run.aggregate_metrics?.total_items;
  if (typeof aggregateCount === 'number' && Number.isFinite(aggregateCount)) return aggregateCount;
  return run.conversations?.length ?? 0;
}

export function isWithinTimeRange(dateValue: string | null | undefined, days: HistoryTimeRange) {
  if (!dateValue) return false;
  const timestamp = new Date(dateValue).getTime();
  if (!Number.isFinite(timestamp)) return false;
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return timestamp >= cutoff;
}

export function getRunHistoryChartData(runs: SimulationBatch[]): HistoryChartPoint[] {
  return [...runs]
    .sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''))
    .map((run) => {
      const score = getSimulationRunAverage(run);
      const agentVersion = getAgentVersionDisplay(run);
      return {
        id: run.id,
        ts: run.created_at ?? run.id,
        dateLabel: formatDate(run.created_at),
        rawDate: run.created_at ?? null,
        score,
        plotScore: score ?? 0,
        status: getStatusLabel(run.status),
        agentVersion: agentVersion.version,
        agentVersionTag: agentVersion.tag,
        populations: run.dataset_names?.length ?? 0,
        conversations: getSimulationConversationCount(run),
      };
    });
}

export function getVersionChartData(runs: SimulationBatch[]): VersionChartPoint[] {
  const byVersion = new Map<string, { scores: number[]; latestRunId: string; latestDate: string }>();

  runs.forEach((run) => {
    const version = getAgentVersionDisplay(run).version;
    if (!version || version === '-') return;

    const existing = byVersion.get(version) ?? {
      scores: [],
      latestRunId: run.id,
      latestDate: run.created_at ?? '',
    };
    const score = getSimulationRunAverage(run);
    if (typeof score === 'number') existing.scores.push(score);
    if ((run.created_at ?? '').localeCompare(existing.latestDate) > 0) {
      existing.latestRunId = run.id;
      existing.latestDate = run.created_at ?? '';
    }
    byVersion.set(version, existing);
  });

  return Array.from(byVersion.entries())
    .sort(([a], [b]) => {
      const aNumber = Number(a.replace(/\D+/g, ''));
      const bNumber = Number(b.replace(/\D+/g, ''));
      if (Number.isFinite(aNumber) && Number.isFinite(bNumber) && aNumber !== bNumber) {
        return aNumber - bNumber;
      }
      return a.localeCompare(b);
    })
    .map(([version, value]) => {
      const score = value.scores.length > 0
        ? Math.round(value.scores.reduce((sum, item) => sum + item, 0) / value.scores.length)
        : null;
      return {
        id: value.latestRunId,
        version,
        score,
        plotScore: score ?? 0,
        runCount: value.scores.length,
      };
    });
}
